import { Grid } from "@mui/material";
import * as React from "react";
import * as messageService from "../../../models/services/messageService";
import { ApplicationPermissionType, GroupPermissionLevel } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { ISessionState } from "../../../models/session/sessionState";
import { IUnitModuleNode } from "../../../models/unit/unitModuleNode";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { IPageNavMenuItem, PageNavMenuItems } from "../../framework/PageNav/PageNavMenuItems";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { UnitModuleSettings } from "source/components/unit/Unit/UnitModuleSettings";
import { MessageTypeList } from "./MessageTypeList";
import { CompanyGroupPermissionSettings } from "source/components/companyGroup/CompanyGroupPermissionSettings";
import { UnitPermissionSettingsNew } from "source/components/unit/Unit/UnitPermissionSettingsNew";

/* eslint-disable no-unused-vars */
export const enum MessageSettingsDialogMenuItem {
    Units = "1",
    Permissions = "2",
    PermissionsOtherCompanies = "3",
    MessageTypes = "4",
}
/* eslint-enable no-unused-vars */

//MessageSettingsDialog
export interface IMessageSettingsDialogProp {
    sessionState: ISessionState;
    companyId: string;
    classes?: string;
    settingsActions: IPageNavMenuItem[];
    onClose: (refreshView: boolean) => void;
}

export interface IMessageSettingsDialogState {
    // Units
    unitModuleNode: IUnitModuleNode;
    // Permissions
    unitNodePermissions: IUnitNodePermissions;

    activeMenuItem: MessageSettingsDialogMenuItem;
}

export class MessageSettingsDialog extends React.Component<IMessageSettingsDialogProp, IMessageSettingsDialogState> {
    private unitsRef = React.createRef<UnitModuleSettings>();
    private permissionsRef = React.createRef<UnitPermissionSettingsNew>();
    private refreshViewOnClose = false;

    constructor(props: IMessageSettingsDialogProp) {
        super(props);
        this.state = {
            unitModuleNode: null,
            unitNodePermissions: null,
            activeMenuItem: props.settingsActions.find(i => i.id === MessageSettingsDialogMenuItem.Units)
                ? MessageSettingsDialogMenuItem.Units
                : MessageSettingsDialogMenuItem.MessageTypes,
        };
    }

    async componentDidMount(): Promise<void> {
        if (this.state.activeMenuItem !== MessageSettingsDialogMenuItem.Units) return;
        await this.loadUnits();
    }

    // #region Units
    onUnitModuleSettingsCancel = async (): Promise<boolean> => {
        return await this.unitsRef.current.handleCancel(false);
    };

    loadUnits = async () => {
        const result = await AppUtils.callService(() => messageService.getUnits(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitModuleNode: result.unitModuleNode
        });
    };

    handleUnitModuleSettingsSave = async (obj: any) => {
        return await messageService.saveUnits(obj);
    };
    // #endregion Units

    // #region Permissions
    onUnitPermissionSettingsCancel = async (): Promise<boolean> => {
        return await this.permissionsRef.current.handleCancel(false);
    };

    loadPermissions = async () => {
        const result = await AppUtils.callService(() => messageService.getUnitNodePermissions(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitNodePermissions: result
        });
    };

    handleUnitPermissionSettingsSave = async (obj: any) => {
        const result = await messageService.saveUnitNodePermissions(obj);
        this.loadPermissions();
        return result;
    };
    // #endregion Permissions

    handleSubViewOk = (_closeDialog: boolean) => {
        this.refreshViewOnClose = true;
    };

    handleToggleActiveMenuItem = async (menuItem: MessageSettingsDialogMenuItem) => {
        const state = this.state;
        if (state.activeMenuItem === menuItem) return;
        //Check current tab cancel
        if (state.activeMenuItem === MessageSettingsDialogMenuItem.Units) {
            if (!await this.onUnitModuleSettingsCancel()) return;
        } else if (state.activeMenuItem === MessageSettingsDialogMenuItem.Permissions) {
            if (!await this.onUnitPermissionSettingsCancel()) return;
        }
        //Load new tab data
        if (menuItem === MessageSettingsDialogMenuItem.Units) {
            await this.loadUnits();
        } else if (menuItem === MessageSettingsDialogMenuItem.Permissions) {
            await this.loadPermissions();
        }
        //Set new tab active
        this.setState({
            activeMenuItem: menuItem
        });
    };

    handleCancel = async (closeDialog: boolean) => {
        if (!closeDialog) return;
        this.props.onClose(this.refreshViewOnClose);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="messageSettingsDialog"
                title={Translations.Settings}
                maxWidth="lg"
                maxHeight="100%"
                onClose={() => this.handleCancel(true)}
                body={
                    <GridRow flexGrow={2}>
                        <Grid item sm={3}>
                            <PageNavMenuItems
                                activeId={state.activeMenuItem}
                                items={props.settingsActions}
                                onToggleMenuItem={this.handleToggleActiveMenuItem}
                            />
                        </Grid>
                        <Grid item sm={9} display="flex" flexDirection="column" flexGrow={2}>
                            {(state.activeMenuItem === MessageSettingsDialogMenuItem.Units) && (
                                <UnitModuleSettings
                                    ref={this.unitsRef}
                                    infoText={Translations.MessageModuleInfoUnits}
                                    companyId={props.companyId}
                                    sessionState={props.sessionState}
                                    applicationPermissionType={ApplicationPermissionType.Message}
                                    unitModuleNode={state.unitModuleNode}
                                    onCancel={this.handleCancel}
                                    onOk={this.handleSubViewOk}
                                    onSave={this.handleUnitModuleSettingsSave}
                                />
                            )}
                            {state.activeMenuItem === MessageSettingsDialogMenuItem.Permissions && (
                                <UnitPermissionSettingsNew
                                    ref={this.permissionsRef}
                                    infoText={Translations.MessageModuleInfoPermissions}
                                    companyId={props.companyId}
                                    unitNodePermissions={state.unitNodePermissions}
                                    cardView={true}
                                    onSave={this.handleUnitPermissionSettingsSave}
                                    onOk={this.handleSubViewOk}
                                    onCancel={this.handleCancel}
                                />
                            )}
                            {state.activeMenuItem === MessageSettingsDialogMenuItem.PermissionsOtherCompanies &&
                                <CompanyGroupPermissionSettings
                                    sessionState={props.sessionState}
                                    companyId={props.companyId}
                                    canIncreasePermissions={true}
                                    levels={[
                                        { number: GroupPermissionLevel.None, title: Translations.AccessRightNone },
                                        { number: GroupPermissionLevel.Read, title: Translations.AccessRightRead },
                                        { number: GroupPermissionLevel.Write, title: Translations.AccessRightWrite }
                                    ]}
                                    maxPermissionLevel={GroupPermissionLevel.Write}
                                />
                            }
                            {state.activeMenuItem === MessageSettingsDialogMenuItem.MessageTypes &&
                                <MessageTypeList
                                    sessionState={props.sessionState}
                                    companyId={props.companyId}
                                />
                            }
                        </Grid>
                    </GridRow>
                }
                buttons={
                    state.activeMenuItem !== MessageSettingsDialogMenuItem.Units && state.activeMenuItem !== MessageSettingsDialogMenuItem.Permissions
                        ? [{ title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                        : null
                }
            />
        );
    }
}